import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { buildStorageReference } from "../lib/storageReferences";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import {
  AlertTriangle,
  Building2,
  Wrench,
  ArrowUpRight,
  ArrowDownRight,
  CalendarDays,
  CircleDollarSign,
  FileText,
  Plus,
  BrainCircuit,
  Home,
  Wallet,
  X,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";

  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Card({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

function CardHeader({ children, className = "" }) {
  return <div className={`p-6 ${className}`}>{children}</div>;
}

function CardContent({ children, className = "" }) {
  return <div className={`px-6 pb-6 ${className}`}>{children}</div>;
}

function CardTitle({ children, className = "" }) {
  return <h3 className={`font-semibold tracking-tight ${className}`}>{children}</h3>;
}

function CardDescription({ children, className = "" }) {
  return <p className={`text-sm ${className}`}>{children}</p>;
}

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

function buildRevenueTrend(properties) {
  const totalRevenue = properties.reduce(
    (sum, item) =>
      sum + Number(item.rent_per_unit || 0) * Number(item.occupied || 0),
    0
  );

  const points = [0.68, 0.74, 0.72, 0.81, 0.88, 0.94, 1];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"];

  return months.map((month, index) => ({
    month,
    revenue: Math.round(totalRevenue * points[index]),
  }));
}

function getPriorityProperty(properties) {
  if (!properties.length) return null;

  const scored = [...properties].sort((a, b) => {
    const aOcc = a.units > 0 ? a.occupied / a.units : 0;
    const bOcc = b.units > 0 ? b.occupied / b.units : 0;

    const aScore = (a.maintenance_open || 0) - aOcc * 10;
    const bScore = (b.maintenance_open || 0) - bOcc * 10;

    return bScore - aScore;
  });

  return scored[0];
}

function getDaysPastDue(dueDate) {
  if (!dueDate) return 0;

  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return 0;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = startOfToday.getTime() - due.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

function getDaysUntilDue(dueDate) {
  if (!dueDate) return null;

  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = due.getTime() - startOfToday.getTime();
  return Math.ceil(diff / 86400000);
}

function extractOrganizationId(profile, user, sessionClaims) {
  return (
    profile?.organization_id ||
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    sessionClaims?.app_metadata?.organization_id ||
    sessionClaims?.app_metadata?.organizationId ||
    sessionClaims?.user_metadata?.organization_id ||
    sessionClaims?.user_metadata?.organizationId ||
    ""
  );
}

function StatCard({ item, index, isDark }) {
  const Icon = item.icon;
  const positive = item.trend === "up";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
    >
      <Card
        className={`rounded-3xl border shadow-md transition-shadow hover:shadow-lg ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDark ? "text-slate-400" : "text-slate-500"
                }`}
              >
                {item.title}
              </p>

              <h3
                className={`mt-3 text-4xl font-semibold tracking-tight ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                {item.value}
              </h3>

              <div className="mt-4 flex items-center gap-3 text-sm">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 font-medium ${
                    positive ? "bg-sky-200 text-sky-800" : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {positive ? (
                    <ArrowUpRight className="h-4 w-4" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4" />
                  )}
                  {item.change}
                </span>

                <span className={isDark ? "text-slate-300" : "text-slate-700"}>
                  vs portfolio
                </span>
              </div>
            </div>

            <div className="rounded-2xl bg-sky-200 p-3 text-sky-800 ring-1 ring-sky-200">
              <Icon className="h-6 w-6" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function ActionCenter({ items, isDark }) {
  const navigate = useNavigate();

  return (
    <section
      className={`rounded-[2rem] border p-5 shadow-md ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
            Today&apos;s Actions
          </p>
          <h2 className={`mt-2 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
            Operational priorities
          </h2>
          <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            Focus on the items most likely to affect collections, occupancy, and service quality.
          </p>
        </div>

        <Badge className={items.length > 0 ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}>
          {items.length > 0 ? `${items.length} open` : "Clear"}
        </Badge>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-2 2xl:grid-cols-4">
        {items.length === 0 ? (
          <div
            className={`rounded-3xl border p-5 text-sm lg:col-span-2 2xl:col-span-4 ${
              isDark
                ? "border-slate-800 bg-slate-950 text-slate-300"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            No urgent action items right now.
          </div>
        ) : (
          items.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.button
                key={item.id}
                type="button"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: index * 0.04 }}
                onClick={() => navigate(item.path)}
                className={`rounded-3xl border p-4 text-left transition ${
                  isDark
                    ? "border-slate-800 bg-slate-950 hover:bg-slate-800"
                    : "border-slate-200 bg-slate-50 hover:bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className={`rounded-2xl p-3 ${item.tone}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="rounded-full bg-sky-100 px-2 py-1 text-[10px] font-semibold text-sky-700">
                    {item.label}
                  </span>
                </div>
                <p className={`mt-4 text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {item.title}
                </p>
                <p className={`mt-2 text-xs leading-5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {item.detail}
                </p>
              </motion.button>
            );
          })
        )}
      </div>
    </section>
  );
}

function CollectionsSnapshot({ summary, isDark, currency }) {
  const navigate = useNavigate();

  const items = [
    {
      label: "Outstanding",
      value: formatMoney(summary.outstanding, currency),
      detail: `${summary.openCount} open invoice${summary.openCount === 1 ? "" : "s"}`,
      tone: "bg-amber-100 text-amber-800",
    },
    {
      label: "Overdue",
      value: formatMoney(summary.overdue, currency),
      detail: `${summary.overdueCount} overdue invoice${summary.overdueCount === 1 ? "" : "s"}`,
      tone: summary.overdue > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700",
    },
    {
      label: "Due in 7 days",
      value: formatMoney(summary.dueSoon, currency),
      detail: `${summary.dueSoonCount} upcoming invoice${summary.dueSoonCount === 1 ? "" : "s"}`,
      tone: "bg-sky-100 text-sky-700",
    },
    {
      label: "Collection rate",
      value: `${summary.collectionRate}%`,
      detail: `${formatMoney(summary.paid, currency)} collected`,
      tone: "bg-emerald-100 text-emerald-700",
    },
  ];

  return (
    <section
      className={`rounded-[2rem] border p-5 shadow-md ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
            Collections Control
          </p>
          <h2 className={`mt-2 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
            Rent collection snapshot
          </h2>
          <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            Paid payments, unpaid balances, and near-term rent follow-up in one view.
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/reports")}
          className={`h-10 rounded-xl px-3 text-sm ${
            isDark
              ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          View reports
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
        {items.map((item) => (
          <div
            key={item.label}
            className={`rounded-3xl border p-4 ${isDark ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-slate-50"}`}
          >
            <Badge className={item.tone}>{item.label}</Badge>
            <p className={`mt-4 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {item.value}
            </p>
            <p className={`mt-2 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {item.detail}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Dashboard({
  isDark,
  setIsDark,
  currency,
  setCurrency,
}) {
  const { profile, user, sessionClaims } = useAuth();
  const [properties, setProperties] = useState([]);
  const [actionData, setActionData] = useState({
    invoices: [],
    payments: [],
    paidPayments: [],
    tickets: [],
    leases: [],
  });
  const [loading, setLoading] = useState(true);
  const [isNewRecordOpen, setIsNewRecordOpen] = useState(false);
  const currentOrganizationId = extractOrganizationId(profile, user, sessionClaims);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);

    if (!currentOrganizationId) {
      setProperties([]);
      setActionData({
        invoices: [],
        payments: [],
        paidPayments: [],
        tickets: [],
        leases: [],
      });
      setLoading(false);
      return;
    }

    const today = new Date();
    const todayText = today.toISOString().slice(0, 10);
    const leaseWindowEnd = new Date(today);
    leaseWindowEnd.setDate(today.getDate() + 30);

    let propertiesQuery = supabase
      .from("properties")
      .select("*")
      .order("created_at", { ascending: false });
    let invoicesQuery = supabase
      .from("invoices")
      .select("id, tenant, property, due_date, amount, status")
      .in("status", ["Outstanding", "Partial", "Overdue"])
      .order("due_date", { ascending: true })
      .limit(25);
    let paymentsQuery = supabase
      .from("payments")
      .select("id, tenant_name, property_name, due_date, amount, status")
      .in("status", ["Pending", "Overdue"])
      .limit(6);
    let paidPaymentsQuery = supabase
      .from("payments")
      .select("invoice_id, amount, status")
      .eq("status", "Paid");
    let ticketsQuery = supabase
      .from("maintenance_tickets")
      .select("id, issue, property, priority, status")
      .in("priority", ["High", "Urgent"])
      .not("status", "eq", "Closed")
      .limit(6);
    let leasesQuery = supabase
      .from("tenants")
      .select("id, full_name, property_name, lease_end, status")
      .gte("lease_end", todayText)
      .lte("lease_end", leaseWindowEnd.toISOString().slice(0, 10))
      .limit(6);

    if (currentOrganizationId) {
      propertiesQuery = propertiesQuery.eq("organization_id", currentOrganizationId);
      invoicesQuery = invoicesQuery.eq("organization_id", currentOrganizationId);
      paymentsQuery = paymentsQuery.eq("organization_id", currentOrganizationId);
      paidPaymentsQuery = paidPaymentsQuery.eq("organization_id", currentOrganizationId);
      ticketsQuery = ticketsQuery.eq("organization_id", currentOrganizationId);
      leasesQuery = leasesQuery.eq("organization_id", currentOrganizationId);
    }

    const [
      { data: propertyData, error: propertyError },
      { data: invoiceData, error: invoiceError },
      { data: paymentData, error: paymentError },
      { data: paidPaymentData, error: paidPaymentError },
      { data: ticketData, error: ticketError },
      { data: leaseData, error: leaseError },
    ] = await Promise.all([
      propertiesQuery,
      invoicesQuery,
      paymentsQuery,
      paidPaymentsQuery,
      ticketsQuery,
      leasesQuery,
    ]);

    if (propertyError) {
      console.error("Error fetching dashboard properties:", propertyError);
    } else {
      setProperties(propertyData || []);
    }

    if (invoiceError) console.error("Error fetching dashboard invoices:", invoiceError);
    if (paymentError) console.error("Error fetching dashboard payments:", paymentError);
    if (paidPaymentError) console.error("Error fetching dashboard paid payments:", paidPaymentError);
    if (ticketError) console.error("Error fetching dashboard tickets:", ticketError);
    if (leaseError) console.error("Error fetching dashboard leases:", leaseError);

    setActionData({
      invoices: invoiceData || [],
      payments: paymentData || [],
      paidPayments: paidPaymentData || [],
      tickets: ticketData || [],
      leases: leaseData || [],
    });

    setLoading(false);
  }, [currentOrganizationId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const totals = useMemo(() => {
    const totalRevenue = properties.reduce(
      (sum, item) =>
        sum + Number(item.rent_per_unit || 0) * Number(item.occupied || 0),
      0
    );

    const totalUnits = properties.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalOccupied = properties.reduce(
      (sum, item) => sum + (item.occupied || 0),
      0
    );
    const totalMaintenance = properties.reduce(
      (sum, item) => sum + (item.maintenance_open || 0),
      0
    );

    const occupancyRate =
      totalUnits > 0 ? ((totalOccupied / totalUnits) * 100).toFixed(1) : "0.0";

    return {
      totalRevenue,
      totalUnits,
      totalOccupied,
      totalMaintenance,
      occupancyRate,
      activeProperties: properties.length,
    };
  }, [properties]);

  const revenueData = useMemo(() => buildRevenueTrend(properties), [properties]);
  const priorityProperty = useMemo(() => getPriorityProperty(properties), [properties]);
  const collectionsSummary = useMemo(() => {
    const paidByInvoice = actionData.paidPayments.reduce((totalsByInvoice, payment) => {
      if (payment.invoice_id) {
        totalsByInvoice[payment.invoice_id] =
          (totalsByInvoice[payment.invoice_id] || 0) + Number(payment.amount || 0);
      }
      return totalsByInvoice;
    }, {});

    const receivables = actionData.invoices
      .map((invoice) => {
        const amount = Number(invoice.amount || 0);
        const paidAmount = Number(paidByInvoice[invoice.id] || 0);
        const balance = Math.max(amount - paidAmount, 0);
        const daysPastDue = getDaysPastDue(invoice.due_date);
        const daysUntilDue = getDaysUntilDue(invoice.due_date);

        return {
          ...invoice,
          amount,
          paidAmount,
          balance,
          daysPastDue,
          daysUntilDue,
        };
      })
      .filter((invoice) => invoice.balance > 0);

    const invoiceTotal = actionData.invoices.reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0);
    const paid = Object.values(paidByInvoice).reduce((sum, amount) => sum + Number(amount || 0), 0);
    const outstanding = receivables.reduce((sum, invoice) => sum + invoice.balance, 0);
    const overdueInvoices = receivables.filter((invoice) => invoice.daysPastDue > 0);
    const dueSoonInvoices = receivables.filter(
      (invoice) => invoice.daysUntilDue !== null && invoice.daysUntilDue >= 0 && invoice.daysUntilDue <= 7
    );

    return {
      paid,
      outstanding,
      overdue: overdueInvoices.reduce((sum, invoice) => sum + invoice.balance, 0),
      dueSoon: dueSoonInvoices.reduce((sum, invoice) => sum + invoice.balance, 0),
      openCount: receivables.length,
      overdueCount: overdueInvoices.length,
      dueSoonCount: dueSoonInvoices.length,
      collectionRate: invoiceTotal > 0 ? Math.round((paid / invoiceTotal) * 100) : 0,
      topReceivables: [...receivables].sort((a, b) => b.daysPastDue - a.daysPastDue || b.balance - a.balance),
    };
  }, [actionData.invoices, actionData.paidPayments]);
  const actionItems = useMemo(() => {
    const vacantProperties = properties
      .filter((property) => Number(property.units || 0) > Number(property.occupied || 0))
      .slice(0, 3)
      .map((property) => ({
        id: `vacancy-${property.id}`,
        label: "Vacancy",
        title: `${Number(property.units || 0) - Number(property.occupied || 0)} vacant unit(s)`,
        detail: `${property.name} has occupancy space available.`,
        path: "/properties",
        icon: Home,
        tone: "bg-sky-100 text-sky-700",
      }));

    return [
      ...collectionsSummary.topReceivables.slice(0, 3).map((invoice) => ({
        id: `invoice-${invoice.id}`,
        label: "Invoice",
        title: invoice.daysPastDue > 0 ? `${invoice.daysPastDue} days overdue` : `Due ${invoice.due_date}`,
        detail: `${invoice.tenant} owes ${formatMoney(invoice.balance, currency)} for ${invoice.property}.`,
        path: "/invoices",
        icon: FileText,
        tone: invoice.daysPastDue > 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800",
      })),
      ...actionData.payments.slice(0, 2).map((payment) => ({
        id: `payment-${payment.id}`,
        label: "Payment",
        title: `${payment.status} payment`,
        detail: `${payment.tenant_name} at ${payment.property_name} needs follow-up.`,
        path: "/payments",
        icon: Wallet,
        tone: "bg-orange-100 text-orange-700",
      })),
      ...actionData.tickets.slice(0, 2).map((ticket) => ({
        id: `ticket-${ticket.id}`,
        label: "Ticket",
        title: `${ticket.priority} maintenance ticket`,
        detail: `${ticket.issue} at ${ticket.property}.`,
        path: "/maintenance",
        icon: AlertTriangle,
        tone: "bg-rose-100 text-rose-700",
      })),
      ...actionData.leases.slice(0, 2).map((tenant) => ({
        id: `lease-${tenant.id}`,
        label: "Lease",
        title: "Lease ending soon",
        detail: `${tenant.full_name} at ${tenant.property_name} ends ${tenant.lease_end}.`,
        path: "/tenants",
        icon: CalendarDays,
        tone: "bg-sky-100 text-sky-700",
      })),
      ...vacantProperties,
    ].slice(0, 8);
  }, [actionData, properties, currency, collectionsSummary.topReceivables]);

  const stats = [
    {
      title: "Outstanding Balance",
      value: formatMoney(collectionsSummary.outstanding, currency),
      change: `${collectionsSummary.openCount} open`,
      trend: collectionsSummary.overdue > 0 ? "down" : "up",
      icon: CircleDollarSign,
    },
    {
      title: "Occupancy Rate",
      value: `${totals.occupancyRate}%`,
      change: "+2.1%",
      trend: "up",
      icon: Building2,
    },
    {
      title: "Active Properties",
      value: `${totals.activeProperties}`,
      change: "+3",
      trend: "up",
      icon: Home,
    },
    {
      title: "Open Maintenance",
      value: `${totals.totalMaintenance}`,
      change: "-8.3%",
      trend: "down",
      icon: Wrench,
    },
  ];

  if (loading) {
    return (
      <AppLayout
        isDark={isDark}
        setIsDark={setIsDark}
        currency={currency}
        setCurrency={setCurrency}
        eyebrow="Executive Dashboard"
        title="Portfolio overview"
        description="A focused summary of portfolio performance, collections, and operational priorities."
        actions={
          <Button
            onClick={() => setIsNewRecordOpen(true)}
            className="h-12 rounded-2xl px-5"
          >
            <Plus className="mr-2 h-4 w-4" />
            New record
          </Button>
        }
      >
        <div className={isDark ? "text-slate-300" : "text-slate-600"}>
          Loading dashboard...
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Executive Dashboard"
      title="Portfolio overview"
      description="A focused summary of portfolio performance, collections, and operational priorities."
      actions={
        <Button
          onClick={() => setIsNewRecordOpen(true)}
          className="h-12 rounded-2xl px-5"
        >
          <Plus className="mr-2 h-4 w-4" />
          New record
        </Button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {stats.map((item, index) => (
          <StatCard key={item.title} item={item} index={index} isDark={isDark} />
        ))}
      </section>

      <CollectionsSnapshot summary={collectionsSummary} isDark={isDark} currency={currency} />

      <ActionCenter items={actionItems} isDark={isDark} />

      <section className="grid gap-6 2xl:grid-cols-[1.45fr_0.55fr]">
        <Card
          className={`rounded-[2rem] border shadow-md transition-shadow hover:shadow-lg ${
            isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
          }`}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className={`text-xl ${isDark ? "text-white" : "text-slate-900"}`}>
                Portfolio performance
              </CardTitle>
              <CardDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
                Snapshot of live revenue and operations from your properties table.
              </CardDescription>
            </div>
            <Badge className="border-0 bg-sky-100 text-sky-700">Live data</Badge>
          </CardHeader>

          <CardContent className="space-y-5">
            <div
              className={`rounded-3xl border p-5 ${
                isDark
                  ? "border-slate-800 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800"
                  : "border-slate-200 bg-gradient-to-br from-sky-50 via-white to-orange-50"
              }`}
            >
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                    Revenue trend
                  </p>
                  <p className={`mt-1 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {formatMoney(totals.totalRevenue, currency)}
                  </p>
                </div>

                <div className="text-right">
                  <Badge className="border-0 bg-orange-100 text-orange-700">
                    {properties.length} properties
                  </Badge>
                  <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Live portfolio trend
                  </p>
                </div>
              </div>

              <div
                className={`rounded-3xl border p-4 shadow-sm ${
                  isDark ? "border-slate-800 bg-slate-950/70" : "border-white/70 bg-white/80"
                }`}
              >
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={revenueData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="revenueStroke" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#0ea5e9" />
                          <stop offset="100%" stopColor="#f97316" />
                        </linearGradient>
                        <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="rgba(14,165,233,0.25)" />
                          <stop offset="100%" stopColor="rgba(249,115,22,0.04)" />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="4 6" stroke="#334155" vertical={false} />

                      <XAxis
                        dataKey="month"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 12 }}
                      />

                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: isDark ? "#94a3b8" : "#64748b", fontSize: 12 }}
                        tickFormatter={(value) => formatMoney(value, currency)}
                        width={100}
                      />

                      <Tooltip
                        cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload || !payload.length) return null;

                          return (
                            <div className="min-w-[210px] rounded-2xl border border-slate-800 bg-slate-950/95 px-4 py-3 text-white shadow-2xl backdrop-blur-sm">
                              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                {label} 2026
                              </p>

                              <div className="mt-3">
                                <p className="text-xs text-slate-400">Estimated revenue</p>
                                <p className="text-2xl font-semibold tracking-tight text-white">
                                  {formatMoney(Number(payload[0].value || 0), currency)}
                                </p>
                              </div>
                            </div>
                          );
                        }}
                      />

                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="url(#revenueStroke)"
                        fill="url(#revenueFill)"
                        strokeWidth={3.5}
                        dot={{ r: 3, fill: "#f97316", stroke: "#ffffff", strokeWidth: 2 }}
                        activeDot={{ r: 7, fill: "#0ea5e9", stroke: "#ffffff", strokeWidth: 3 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {properties.slice(0, 3).map((property, i) => {
              const propertyRevenue =
                Number(property.rent_per_unit || 0) * Number(property.occupied || 0);

              return (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className={`rounded-3xl border p-5 ${
                    isDark
                      ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
                      : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
                  }`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                        {property.name}
                      </h3>
                      <p className={`mt-1 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                        {property.occupied}/{property.units} units occupied
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      <div
                        className={`rounded-2xl border px-4 py-3 ${
                          isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-white"
                        }`}
                      >
                        <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                          Revenue
                        </p>
                        <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {formatMoney(propertyRevenue, currency)}
                        </p>
                      </div>

                      <div
                        className={`rounded-2xl border px-4 py-3 ${
                          isDark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-white"
                        }`}
                      >
                        <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                          Occupancy
                        </p>
                        <p className={`mt-1 font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {property.units > 0
                            ? Math.round((property.occupied / property.units) * 100)
                            : 0}
                          %
                        </p>
                      </div>

                      <div
                        className={`hidden rounded-2xl border px-4 py-3 md:block ${
                          isDark
                            ? "border-slate-700 bg-slate-800 text-sky-400"
                            : "border-sky-100 bg-sky-50 text-sky-700"
                        }`}
                      >
                        <p className={`text-xs uppercase tracking-wide ${isDark ? "text-slate-400" : "text-sky-600"}`}>
                          Status
                        </p>
                        <p className="mt-1 font-semibold">{property.status}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </CardContent>
        </Card>

        <Card
          className={`rounded-[2rem] border shadow-md transition-shadow hover:shadow-lg ${
            isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
          }`}
        >
          <CardHeader>
            <CardTitle className={`text-xl ${isDark ? "text-white" : "text-slate-900"}`}>
              Executive brief
            </CardTitle>
            <CardDescription className={isDark ? "text-slate-300" : "text-slate-600"}>
              Live signals generated from your active property portfolio.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div
              className={`rounded-3xl border p-4 ${
                isDark
                  ? "border-slate-800 bg-slate-900"
                  : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
              }`}
            >
              <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                Priority property
              </p>
              <p className={`mt-2 text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {priorityProperty?.name || "No properties yet"}
              </p>
              <p className={`mt-2 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                {priorityProperty
                  ? `${priorityProperty.location} currently has ${priorityProperty.maintenance_open} open maintenance items and ${
                      priorityProperty.units > 0
                        ? Math.round((priorityProperty.occupied / priorityProperty.units) * 100)
                        : 0
                    }% occupancy.`
                  : "Add properties to generate portfolio insights."}
              </p>
            </div>

            <div
              className={`rounded-3xl border p-4 ${
                isDark ? "border-slate-800 bg-slate-900" : "border-sky-100 bg-sky-50"
              }`}
            >
              <div className="mb-2 flex items-center gap-2 text-sky-700">
                <BrainCircuit className="h-5 w-5" />
                <span className="font-medium">AI recommendation</span>
              </div>

              <p className={`text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {priorityProperty
                  ? `Prioritize ${priorityProperty.name}, review maintenance backlog, and strengthen occupancy performance to improve portfolio stability.`
                  : "Once property data is available, AI recommendations will appear here."}
              </p>
            </div>

            <div
              className={`rounded-3xl border p-4 ${
                isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
              }`}
            >
              <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                Suggested action
              </p>
              <p className={`mt-2 text-base font-medium ${isDark ? "text-white" : "text-slate-900"}`}>
                {priorityProperty
                  ? `Review ${priorityProperty.name} performance plan`
                  : "Create your first property record"}
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      <NewPropertyModal
        isOpen={isNewRecordOpen}
        onClose={() => setIsNewRecordOpen(false)}
        onSaved={fetchDashboardData}
        isDark={isDark}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
      />
    </AppLayout>
  );
}

const PROPERTY_IMAGE_BUCKET = "property-images";

function getPropertyImagePath(file) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");

  return `dashboard-new-property/${Date.now()}-${safeName}`;
}

async function uploadPropertyImage(file) {
  const path = getPropertyImagePath(file);
  const { error } = await supabase.storage
    .from(PROPERTY_IMAGE_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (error) throw error;

  return buildStorageReference(PROPERTY_IMAGE_BUCKET, path);
}

function NewPropertyModal({ isOpen, onClose, onSaved, isDark, currency, currentOrganizationId }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    location: "",
    type: "Apartments",
    units: "",
    occupied: "",
    rent_per_unit: "",
    maintenance_open: "",
    manager: "",
    property_image_url: "",
    status: "Stable",
  });
  const [saving, setSaving] = useState(false);
  const [propertyImageFile, setPropertyImageFile] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      name: "",
      location: "",
      type: "Apartments",
      units: "",
      occupied: "",
      rent_per_unit: "",
      maintenance_open: "",
      manager: "",
      property_image_url: "",
      status: "Stable",
    });
    setPropertyImageFile(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    let propertyImageUrl = "";

    try {
      if (propertyImageFile) {
        propertyImageUrl = await uploadPropertyImage(propertyImageFile);
      }
    } catch (error) {
      setSaving(false);
      console.error("Dashboard property image upload error:", error);
      toast.error(
        "Property image upload failed",
        "Please check your storage bucket configuration and try again."
      );
      return;
    }

    const payload = {
      name: form.name,
      location: form.location,
      type: form.type,
      units: Number(form.units || 0),
      occupied: Number(form.occupied || 0),
      rent_per_unit: Number(form.rent_per_unit || 0),
      maintenance_open: Number(form.maintenance_open || 0),
      manager: form.manager,
      property_image_url: propertyImageUrl,
      status: form.status,
      organization_id: currentOrganizationId || null,
    };

    const { error } = await supabase.from("properties").insert([payload]);

    setSaving(false);

    if (error) {
      console.error("Create property from dashboard error:", error);
      toast.error("Property record was not created", "Please review the details and try again.");
      return;
    }

    toast.success("Property created", `${form.name} is now part of your portfolio dashboard.`);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              New Property Record
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Add a property and update the dashboard snapshot.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <Input
            placeholder="Property name"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Input
            placeholder="Location"
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Select
            value={form.type}
            onChange={(e) => update("type", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
          >
            <option>Apartments</option>
            <option>Serviced Suites</option>
            <option>Townhouses</option>
            <option>Mixed Use</option>
            <option>Lofts</option>
          </Select>

          <Select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"}
          >
            <option>High performing</option>
            <option>Stable</option>
            <option>Needs attention</option>
          </Select>

          <Input
            type="number"
            placeholder="Total units"
            value={form.units}
            onChange={(e) => update("units", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Input
            type="number"
            placeholder="Occupied units"
            value={form.occupied}
            onChange={(e) => update("occupied", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Input
            type="number"
            placeholder={`Rent per unit (${currency})`}
            value={form.rent_per_unit}
            onChange={(e) => update("rent_per_unit", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <Input
            type="number"
            placeholder="Open maintenance"
            value={form.maintenance_open}
            onChange={(e) => update("maintenance_open", e.target.value)}
            className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            required
          />

          <div className="md:col-span-2">
            <Input
              placeholder="Manager"
              value={form.manager}
              onChange={(e) => update("manager", e.target.value)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Property image
            </label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setPropertyImageFile(e.target.files?.[0] || null)}
              className={isDark ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500" : "border-slate-200 bg-white text-slate-900"}
            />
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {propertyImageFile
                ? `Ready to upload: ${propertyImageFile.name}`
                : "Attach a property image from your device."}
            </p>
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? "Uploading..." : "Create Record"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
